/**
 * Cookie Banner Component
 * Floating bottom banner shown on first visit - visually appealing with rounded edges
 */

import { useEffect, useState } from 'react';
import { Cookie, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BANNER_CONTENT } from '../constants';

interface CookieBannerProps {
  /** Whether the banner should be visible */
  visible: boolean;
  /** Called when user clicks "Settings" */
  onOpenSettings: () => void;
  /** Called when user clicks "Accept All" */
  onAcceptAll: () => void;
}

export function CookieBanner({ visible, onOpenSettings, onAcceptAll }: CookieBannerProps) {
  const [mounted, setMounted] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);

  // Handle mount animation
  useEffect(() => {
    if (visible && !mounted) {
      setMounted(true);
      // Slight delay for slide-up animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimateIn(true);
        });
      });
    } else if (!visible && mounted) {
      setAnimateIn(false);
      // Wait for animation before unmounting
      const timer = setTimeout(() => setMounted(false), 400);
      return () => clearTimeout(timer);
    }
  }, [visible, mounted]);

  if (!mounted) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className={cn(
        'fixed bottom-0 left-0 right-0 z-50 p-4 tablet:p-6',
        'transition-all duration-500 ease-out',
        animateIn
          ? 'translate-y-0 opacity-100'
          : 'translate-y-8 opacity-0'
      )}
    >
      {/* Floating glassmorphism container - wider and shorter */}
      <div
        className={cn(
          'mx-auto max-w-6xl',
          // Glassmorphism with strong blur
          'bg-white/90 backdrop-blur-2xl',
          // Rounded edges - more pronounced
          'rounded-2xl tablet:rounded-3xl',
          // Beautiful shadow
          'shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_1px_rgba(255,255,255,0.8)_inset]',
          // Border
          'border border-gray-200/60',
          // Subtle animation
          'transition-shadow duration-300 hover:shadow-[0_12px_50px_rgba(0,0,0,0.15),0_0_0_1px_rgba(255,255,255,0.9)_inset]'
        )}
      >
        <div className="p-4 tablet:px-6 tablet:py-4">
          {/* Mobile layout - compact */}
          <div className="tablet:hidden">
            <div className="flex items-center gap-3 mb-4">
              {/* Cookie icon with gradient background */}
              <div className="shrink-0 p-2.5 rounded-xl bg-gradient-to-br from-[#014383]/10 via-[#0b5cab]/10 to-[#55a5db]/10">
                <Cookie className="h-5 w-5 text-[#014383]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-bold text-gray-900">
                  {BANNER_CONTENT.title}
                </h3>
                <p className="text-xs text-gray-600 leading-snug line-clamp-2">
                  {BANNER_CONTENT.description}
                </p>
              </div>
            </div>

            {/* Buttons side by side on mobile */}
            <div className="flex gap-2.5">
              <button
                type="button"
                onClick={onOpenSettings}
                className={cn(
                  'flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm',
                  'bg-gray-100/80 text-gray-700 border border-gray-200',
                  'hover:bg-gray-200/80 hover:border-gray-300',
                  'active:scale-[0.98] transition-all duration-200',
                  'flex items-center justify-center gap-1.5'
                )}
              >
                <Settings className="h-4 w-4" />
                {BANNER_CONTENT.buttons.settings}
              </button>
              <button
                type="button"
                onClick={onAcceptAll}
                className={cn(
                  'flex-1 px-4 py-2.5 rounded-xl font-bold text-sm',
                  // Header matching gradient
                  'bg-gradient-to-r from-[#014383] to-[#0159a8] text-white',
                  'hover:from-[#003d7a] hover:to-[#014383]',
                  'shadow-md shadow-[#014383]/20',
                  'active:scale-[0.98] transition-all duration-200'
                )}
              >
                {BANNER_CONTENT.buttons.acceptAll}
              </button>
            </div>
          </div>

          {/* Tablet+ layout - compact single row */}
          <div className="hidden tablet:flex tablet:items-center tablet:justify-between tablet:gap-6">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              {/* Cookie icon with gradient background */}
              <div className="shrink-0 p-2.5 rounded-xl bg-gradient-to-br from-[#014383]/10 via-[#0b5cab]/10 to-[#55a5db]/10">
                <Cookie className="h-6 w-6 text-[#014383]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-bold text-gray-900">
                  {BANNER_CONTENT.title}
                </h3>
                <p className="text-sm text-gray-600 mt-0.5">
                  {BANNER_CONTENT.description}
                </p>
              </div>
            </div>

            {/* Buttons inline on tablet+ */}
            <div className="flex items-center gap-2.5 shrink-0">
              <button
                type="button"
                onClick={onOpenSettings}
                className={cn(
                  'px-4 py-2.5 rounded-xl font-semibold text-sm',
                  'bg-gray-100/80 text-gray-700 border border-gray-200',
                  'hover:bg-gray-200/80 hover:border-gray-300',
                  'active:scale-[0.98] transition-all duration-200',
                  'flex items-center gap-1.5'
                )}
              >
                <Settings className="h-4 w-4" />
                {BANNER_CONTENT.buttons.settings}
              </button>
              <button
                type="button"
                onClick={onAcceptAll}
                className={cn(
                  'px-5 py-2.5 rounded-xl font-bold text-sm',
                  // Header matching gradient
                  'bg-gradient-to-r from-[#014383] to-[#0159a8] text-white',
                  'hover:from-[#003d7a] hover:to-[#014383]',
                  'shadow-md shadow-[#014383]/20',
                  'active:scale-[0.98] transition-all duration-200'
                )}
              >
                {BANNER_CONTENT.buttons.acceptAll}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
